import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Loader2, Utensils, Activity, Mail } from 'lucide-react';
import { jsPDF } from 'jspdf';
import { productAPI } from '../lib/api';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SEO from '../components/SEO';

// Helper to get localized text from product fields
const getLocalizedText = (field, language) => {
    if (!field) return '';
    if (typeof field === 'string') return field;
    return field[language] || field['en'] || field['no'] || '';
};

// Helper to get localized array from product fields
const getLocalizedArray = (field, language) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    return field[language] || field['en'] || field['no'] || [];
};

const ProductDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [generatingPdf, setGeneratingPdf] = useState(false);

    useEffect(() => {
        const fetchProduct = async () => {
            try {
                const res = await productAPI.getById(id);
                setProduct(res.data);
            } catch (error) {
                console.error('Failed to fetch product:', error);
                toast.error(t('productNotFound'));
                navigate('/products');
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id, navigate, t]);

    const generateProductPDF = async () => {
        if (!product) return;
        
        setGeneratingPdf(true);
        
        try {
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });

            const pageWidth = 210;
            const pageHeight = 297;
            const margin = 15;
            
            // Layout: Left side for text (half page), Right side for image
            const centerX = pageWidth / 2;
            const leftContentWidth = centerX - margin - 5;
            const rightStartX = centerX + 5;
            const rightContentWidth = pageWidth - rightStartX - margin;

            // Colors
            const darkGray = [35, 35, 35];
            const mediumGray = [80, 80, 80];
            const accentBlue = [52, 73, 94];

            // Load product image and convert to JPEG with white background to fix transparency
            let imgData = null;
            try {
                const API_URL = process.env.REACT_APP_BACKEND_URL || '';
                const proxyResponse = await fetch(`${API_URL}/api/image-proxy?url=${encodeURIComponent(product.image)}`);
                const proxyData = await proxyResponse.json();
                
                if (proxyData.success && proxyData.data) {
                    // Convert PNG with transparency to JPEG with white background
                    const img = new Image();
                    await new Promise((resolve) => {
                        img.onload = () => {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.width;
                            canvas.height = img.height;
                            const ctx = canvas.getContext('2d');
                            // Fill with white background first
                            ctx.fillStyle = '#FFFFFF';
                            ctx.fillRect(0, 0, canvas.width, canvas.height);
                            // Draw image on top
                            ctx.drawImage(img, 0, 0);
                            imgData = canvas.toDataURL('image/jpeg', 0.95);
                            resolve();
                        };
                        img.onerror = resolve;
                        img.src = proxyData.data;
                    });
                }
            } catch (e) {
                console.log('Could not load product image for PDF:', e);
            }

            // Header with dark background
            pdf.setFillColor(35, 35, 35);
            pdf.rect(0, 0, pageWidth, 40, 'F');
            
            pdf.setTextColor(255, 255, 255);
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(22);
            pdf.text(product.name.toUpperCase(), margin, 20);
            
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            pdf.setTextColor(200, 200, 200);
            pdf.text(`${getLocalizedText(product.origin, language)} · ${product.bottle_size}`, margin, 30);

            // === RIGHT SIDE: Product Image ===
            let yPos = 50;
            const imgStartY = yPos;
            
            if (imgData) {
                const img = new Image();
                let actualImgWidth = rightContentWidth;
                let actualImgHeight = 100; // Slightly smaller image to make room
                
                await new Promise((resolve) => {
                    img.onload = () => {
                        const aspectRatio = img.width / img.height;
                        // Calculate dimensions to fit in right column
                        if (aspectRatio > (rightContentWidth / 100)) {
                            actualImgWidth = rightContentWidth;
                            actualImgHeight = rightContentWidth / aspectRatio;
                        } else {
                            actualImgHeight = 100;
                            actualImgWidth = 100 * aspectRatio;
                        }
                        resolve();
                    };
                    img.onerror = resolve;
                    img.src = imgData;
                });
                
                // Center image in right column
                const imgX = rightStartX + (rightContentWidth - actualImgWidth) / 2;
                pdf.addImage(imgData, 'JPEG', imgX, imgStartY, actualImgWidth, actualImgHeight, undefined, 'MEDIUM');
            }

            // === LEFT SIDE: All text content ===
            let leftYPos = yPos;

            // === SECTION 1: Unique Selling Points ===
            const uniquePoints = getLocalizedArray(product.unique_selling_points, language);
            if (uniquePoints.length > 0) {
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(8);
                pdf.setTextColor(...darkGray);
                pdf.text(language === 'no' ? 'HVA GJØR DETTE VANNET UNIKT?' : 'WHAT MAKES THIS WATER UNIQUE?', margin, leftYPos);
                leftYPos += 5;

                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(7);
                pdf.setTextColor(...mediumGray);
                uniquePoints.forEach((point, index) => {
                    const pointLines = pdf.splitTextToSize(`${index + 1}. ${point}`, leftContentWidth);
                    pdf.text(pointLines, margin, leftYPos);
                    leftYPos += pointLines.length * 3.5 + 1.5;
                });
                leftYPos += 3;
            }

            // === SECTION 2: Body Functions ===
            const bodyFunctions = getLocalizedArray(product.body_functions, language);
            if (bodyFunctions.length > 0) {
                pdf.setDrawColor(...accentBlue);
                pdf.setLineWidth(0.3);
                pdf.line(margin, leftYPos, margin + 15, leftYPos);
                leftYPos += 4;

                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(8);
                pdf.setTextColor(...darkGray);
                pdf.text(language === 'no' ? 'KROPPSFUNKSJONER' : 'BODY FUNCTIONS', margin, leftYPos);
                leftYPos += 4;

                pdf.setFontSize(6);
                bodyFunctions.forEach((func) => {
                    pdf.setTextColor(...darkGray);
                    pdf.setFont('helvetica', 'bold');
                    const funcName = `• ${func.name}`;
                    pdf.text(funcName, margin, leftYPos);
                    pdf.setFont('helvetica', 'normal');
                    pdf.setTextColor(...mediumGray);
                    const descLines = pdf.splitTextToSize(func.description, leftContentWidth - 3);
                    pdf.text(descLines[0], margin + 3, leftYPos + 3);
                    leftYPos += 6;
                });
                leftYPos += 2;
            }

            // === SECTION 4: Water Profile + Mineral Analysis (full width, below image) ===
            yPos = Math.max(leftYPos, imgStartY + 110) + 3;
            
            // Water Profile section
            const mineralProfile = product.mineral_profile;
            if (mineralProfile) {
                pdf.setDrawColor(...accentBlue);
                pdf.setLineWidth(0.3);
                pdf.line(margin, yPos, margin + 15, yPos);
                yPos += 4;

                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(8);
                pdf.setTextColor(...darkGray);
                pdf.text(language === 'no' ? 'VANNPROFIL' : 'WATER PROFILE', margin, yPos);
                yPos += 5;

                const profileLabels = {
                    minerality: language === 'no' ? 'Mineralitet' : 'Minerality',
                    hardness_level: language === 'no' ? 'Hardhet' : 'Hardness',
                    carbonation: language === 'no' ? 'Kullsyre' : 'Carbonation'
                };

                // Display profile - only minerality, hardness, carbonation (filter out orientation and vintage)
                const profileEntries = Object.entries(mineralProfile).filter(([key]) => 
                    ['minerality', 'hardness_level', 'carbonation'].includes(key)
                );
                const colWidth = (pageWidth - margin * 2) / 2 - 5;
                
                pdf.setFontSize(6);
                profileEntries.forEach(([key, value], index) => {
                    const col = index % 2;
                    const row = Math.floor(index / 2);
                    const x = margin + col * (colWidth + 10);
                    const y = yPos + row * 4;
                    
                    pdf.setFont('helvetica', 'normal');
                    pdf.setTextColor(...mediumGray);
                    pdf.text(profileLabels[key] || key, x, y);
                    
                    pdf.setFont('helvetica', 'bold');
                    pdf.setTextColor(...darkGray);
                    const profileValue = typeof value === 'object' ? (value[language] || value.en || value.no) : value;
                    pdf.text(profileValue, x + 28, y);
                });
                
                yPos += Math.ceil(profileEntries.length / 2) * 4 + 4;
            }
            
            // Mineral Analysis section
            pdf.setDrawColor(...accentBlue);
            pdf.setLineWidth(0.3);
            pdf.line(margin, yPos, margin + 15, yPos);
            yPos += 5;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.setTextColor(...darkGray);
            pdf.text(language === 'no' ? 'MINERALANALYSE' : 'MINERAL ANALYSIS', margin, yPos);
            yPos += 6;

            const mineralLabels = language === 'no' ? {
                tds: 'TDS',
                ph: 'pH-faktor',
                hardness: 'Hardhet',
                nitrate: 'Nitrat',
                calcium: 'Kalsium',
                magnesium: 'Magnesium',
                sodium: 'Natrium',
                potassium: 'Kalium',
                silica: 'Silika',
                bicarbonate: 'Bikarbonat',
                sulfate: 'Sulfat',
                chloride: 'Klorid',
                fluoride: 'Fluorid'
            } : {
                tds: 'TDS',
                ph: 'pH Factor',
                hardness: 'Hardness',
                nitrate: 'Nitrate',
                calcium: 'Calcium',
                magnesium: 'Magnesium',
                sodium: 'Sodium',
                potassium: 'Potassium',
                silica: 'Silica',
                bicarbonate: 'Bicarbonate',
                sulfate: 'Sulfate',
                chloride: 'Chloride',
                fluoride: 'Fluoride'
            };

            // Mineral table - TWO COLUMN layout to fit all content
            const minerals = Object.entries(product.mineral_content || {});
            const colWidth = (pageWidth - margin * 2 - 10) / 2; // Two columns with gap
            const rowHeight = 6;
            const mineralStartY = yPos;
            
            pdf.setFontSize(7);
            minerals.forEach(([key, value], index) => {
                const col = index % 2;
                const row = Math.floor(index / 2);
                const x = margin + col * (colWidth + 10);
                const y = mineralStartY + row * rowHeight;
                
                // Alternating row background for each column
                if (row % 2 === 0) {
                    pdf.setFillColor(248, 248, 248);
                    pdf.rect(x, y - 4, colWidth, rowHeight, 'F');
                }
                
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(...mediumGray);
                pdf.text(mineralLabels[key] || key, x + 2, y);
                
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(...darkGray);
                pdf.text(value, x + colWidth - 2 - pdf.getTextWidth(value), y);
            });
            
            yPos = mineralStartY + Math.ceil(minerals.length / 2) * rowHeight + 5;

            // Footer
            const footerY = pageHeight - 12;
            pdf.setFillColor(35, 35, 35);
            pdf.rect(0, footerY - 3, pageWidth, 15, 'F');
            
            pdf.setTextColor(255, 255, 255);
            pdf.setFontSize(8);
            pdf.setFont('helvetica', 'bold');
            pdf.text('RAW MINERALS', margin, footerY + 4);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(180, 180, 180);
            pdf.text('www.rawminerals.no', pageWidth - margin - pdf.getTextWidth('www.rawminerals.no'), footerY + 4);

            // Save
            pdf.save(`${product.name}_Produktark.pdf`);
            toast.success(language === 'no' ? 'PDF lastet ned!' : 'PDF downloaded!');
            
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error(language === 'no' ? 'Kunne ikke generere PDF' : 'Failed to generate PDF');
        } finally {
            setGeneratingPdf(false);
        }
    };

    if (loading) {
        return (
            <div className="page-transition py-32">
                <div className="max-w-[1400px] mx-auto px-6 md:px-12">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                        <div className="aspect-square bg-stone-100 animate-pulse" />
                        <div className="space-y-6">
                            <div className="h-8 bg-stone-100 w-1/3 animate-pulse" />
                            <div className="h-12 bg-stone-100 w-2/3 animate-pulse" />
                            <div className="h-24 bg-stone-100 animate-pulse" />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) return null;

    const mineralLabels = {
        calcium: t('calcium'),
        magnesium: t('magnesium'),
        sodium: t('sodium'),
        potassium: t('potassium'),
        bicarbonate: t('bicarbonate'),
        ph: t('ph'),
        tds: t('tds'),
    };

    const uniquePoints = getLocalizedArray(product.unique_selling_points, language);
    const bodyFunctions = getLocalizedArray(product.body_functions, language);
    const foodPairings = getLocalizedArray(product.food_pairings, language);

    return (
        <div className="page-transition" data-testid="product-detail-page">
            <SEO 
                title={product.name}
                description={`${product.name} - ${getLocalizedText(product.description, language).substring(0, 150)}...`}
                canonical={`/products/${product.id}`}
            />
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 md:py-24">
                {/* Back Link */}
                <Link 
                    to="/products"
                    className="inline-flex items-center gap-2 text-stone-500 hover:text-stone-900 text-sm mb-12 transition-colors"
                    data-testid="back-link"
                >
                    <ArrowLeft className="w-4 h-4" />
                    {t('backToCollection')}
                </Link>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-24">
                    {/* Product Image */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                        className="aspect-square bg-stone-50 overflow-hidden flex items-center justify-center"
                    >
                        <img 
                            src={product.image} 
                            alt={product.name}
                            className="w-full h-full object-contain p-4"
                            data-testid="product-image"
                        />
                    </motion.div>

                    {/* Product Info */}
                    <motion.div
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                    >
                        <p className="caption mb-4">{getLocalizedText(product.origin, language)} · {product.category === 'still' ? t('stillWater') : t('sparklingWater')}</p>
                        <h1 
                            className="font-display text-4xl md:text-5xl font-light mb-4"
                            data-testid="product-name"
                        >
                            {product.name}
                        </h1>
                        <a 
                            href="mailto:hello@rawminerals.no?subject=Prisforespørsel - ${product.name}"
                            className="inline-flex items-center gap-2 text-fjord hover:text-fjord/80 transition-colors mb-8 text-sm"
                            data-testid="product-price"
                        >
                            <Mail className="w-4 h-4" />
                            Kontakt for pris og utsalgssteder
                        </a>

                        <p className="text-stone-600 leading-relaxed mb-8">
                            {getLocalizedText(product.description, language)}
                        </p>

                        {/* Download PDF Button */}
                        <Button
                            onClick={generateProductPDF}
                            disabled={generatingPdf}
                            variant="outline"
                            className="w-full mb-12 rounded-none py-4 text-xs uppercase tracking-[0.15em]"
                            data-testid="download-product-pdf"
                        >
                            {generatingPdf ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-3 animate-spin" />
                                    {language === 'no' ? 'Genererer...' : 'Generating...'}
                                </>
                            ) : (
                                <>
                                    <Download className="w-4 h-4 mr-3" />
                                    {language === 'no' ? 'Last ned produktark (PDF)' : 'Download product sheet (PDF)'}
                                </>
                            )}
                        </Button>

                        {/* Unique Selling Points */}
                        {uniquePoints.length > 0 && (
                            <div className="border-t border-stone-200 pt-8">
                                <h3 className="font-display text-lg mb-6">
                                    {language === 'no' ? 'Hva gjør dette vannet unikt?' : 'What makes this water unique?'}
                                </h3>
                                <ul className="space-y-3">
                                    {uniquePoints.map((point, index) => (
                                        <li 
                                            key={index}
                                            className="flex items-start gap-3 text-sm text-stone-600"
                                        >
                                            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-fjord text-white text-xs flex items-center justify-center mt-0.5">
                                                {index + 1}
                                            </span>
                                            {point}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {/* Body Functions */}
                        {bodyFunctions.length > 0 && (
                            <div className="border-t border-stone-200 pt-8 mt-8">
                                <h3 className="font-display text-lg mb-6 flex items-center gap-2">
                                    <Activity className="w-5 h-5 text-fjord" />
                                    {language === 'no' ? 'Kroppsfunksjoner' : 'Body Functions'}
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {bodyFunctions.map((func, index) => (
                                        <div 
                                            key={index}
                                            className="bg-stone-50 p-4 rounded-lg"
                                        >
                                            <h4 className="font-semibold text-sm text-stone-800 mb-1">{func.name}</h4>
                                            <p className="text-xs text-stone-500">{func.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Product Details */}
                        <div className="border-t border-stone-200 pt-8 mt-8">
                            <h3 className="font-display text-lg mb-6">{t('productDetails')}</h3>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                                <div>
                                    <span className="text-stone-500">{t('origin')}</span>
                                    <p className="font-medium">{getLocalizedText(product.origin, language)}</p>
                                </div>
                                <div>
                                    <span className="text-stone-500">{t('bottleSize')}</span>
                                    <p className="font-medium">{product.bottle_size}</p>
                                </div>
                                <div>
                                    <span className="text-stone-500">{t('type')}</span>
                                    <p className="font-medium capitalize">{product.category === 'still' ? t('stillWater') : t('sparklingWater')}</p>
                                </div>
                                <div>
                                    <span className="text-stone-500">{t('phLevel')}</span>
                                    <p className="font-medium">{product.mineral_content?.ph || 'N/A'}</p>
                                </div>
                            </div>
                        </div>

                        {/* Mineral Profile */}
                        {product.mineral_profile && (
                            <div className="border-t border-stone-200 pt-8 mt-8">
                                <h3 className="font-display text-lg mb-6">
                                    {language === 'no' ? 'Vannprofil' : 'Water Profile'}
                                </h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                    {Object.entries(product.mineral_profile)
                                        .filter(([key]) => ['minerality', 'hardness_level', 'carbonation'].includes(key))
                                        .map(([key, value]) => {
                                        const labels = {
                                            minerality: language === 'no' ? 'Mineralitet' : 'Minerality',
                                            hardness_level: language === 'no' ? 'Hardhet' : 'Hardness',
                                            carbonation: language === 'no' ? 'Kullsyre' : 'Carbonation'
                                        };
                                        return (
                                            <div key={key} className="bg-stone-50 p-3 rounded">
                                                <span className="text-xs text-stone-400 uppercase tracking-wider">{labels[key] || key}</span>
                                                <p className="font-semibold text-stone-800 mt-1">{getLocalizedText(value, language)}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* Mineral Content */}
                        <div className="border-t border-stone-200 pt-8 mt-8">
                            <h3 className="font-display text-lg mb-6">{t('mineralAnalysis')}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8">
                                {Object.entries(product.mineral_content || {}).map(([key, value], index) => {
                                    const labels = {
                                        tds: 'TDS',
                                        ph: language === 'no' ? 'pH-faktor' : 'pH Factor',
                                        hardness: language === 'no' ? 'Hardhet' : 'Hardness',
                                        nitrate: language === 'no' ? 'Nitrat' : 'Nitrate',
                                        calcium: language === 'no' ? 'Kalsium' : 'Calcium',
                                        magnesium: 'Magnesium',
                                        sodium: language === 'no' ? 'Natrium' : 'Sodium',
                                        potassium: language === 'no' ? 'Kalium' : 'Potassium',
                                        silica: language === 'no' ? 'Silika' : 'Silica',
                                        bicarbonate: language === 'no' ? 'Bikarbonat' : 'Bicarbonate',
                                        sulfate: language === 'no' ? 'Sulfat' : 'Sulfate',
                                        chloride: language === 'no' ? 'Klorid' : 'Chloride',
                                        fluoride: language === 'no' ? 'Fluorid' : 'Fluoride'
                                    };
                                    return (
                                        <div 
                                            key={key} 
                                            className={`flex justify-between py-2 border-b border-stone-100 ${index % 2 === 0 ? 'bg-stone-50/50' : ''}`}
                                        >
                                            <span className="text-stone-500 text-sm">{labels[key] || key}</span>
                                            <span className="font-mono text-sm font-medium">{value}</span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </div>
    );
};

export default ProductDetailPage;
